import { useCallback, useMemo, useState } from 'react'

const STUDENT_SORT_OPTIONS = [
	{ id: 'student_asc', key: 'student', dir: 'asc', label: 'A → Z' },
	{ id: 'student_desc', key: 'student', dir: 'desc', label: 'Z → A' },
]

const compareText = (a, b) =>
	String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' })

export const useContacts = ({ contactRows = [] }) => {
	const [activeGrade, setActiveGrade] = useState('all')
	const [sortKey, setSortKey] = useState(null)
	const [sortDir, setSortDir] = useState('asc')
	const [expandedIds, setExpandedIds] = useState([])

	const { allCount, gradeTabs } = useMemo(() => {
		const gradeCounts = {}

		contactRows.forEach(row => {
			const grade = Number(row.gradeNumber)
			if (grade && !Number.isNaN(grade) && grade > 0) {
				gradeCounts[grade] = (gradeCounts[grade] || 0) + 1
			}
		})

		return {
			allCount: contactRows.length,
			gradeTabs: Object.entries(gradeCounts)
				.sort((a, b) => Number(a[0]) - Number(b[0]))
				.map(([grade, count]) => ({
					id: `grade-${grade}`,
					label: `Grade ${grade}`,
					count,
				})),
		}
	}, [contactRows])

	const tabs = useMemo(
		() => [{ id: 'all', label: 'All Students', count: allCount }, ...gradeTabs],
		[allCount, gradeTabs],
	)

	const setSort = useCallback((key, dir) => {
		setSortKey(key)
		setSortDir(dir === 'desc' ? 'desc' : 'asc')
	}, [])

	const toggleExpand = useCallback(studentId => {
		const id = String(studentId)
		setExpandedIds(current =>
			current.includes(id) ? current.filter(item => item !== id) : [...current, id],
		)
	}, [])

	const visibleRows = useMemo(() => {
		let rows = contactRows

		if (activeGrade && activeGrade !== 'all' && String(activeGrade).startsWith('grade-')) {
			const gradeNum = Number(String(activeGrade).replace('grade-', ''))
			if (!Number.isNaN(gradeNum)) {
				rows = rows.filter(row => Number(row.gradeNumber) === gradeNum)
			}
		}

		if (sortKey === 'student') {
			rows = [...rows].sort((a, b) => {
				const cmp = compareText(a.studentName, b.studentName)
				return sortDir === 'desc' ? -cmp : cmp
			})
		}

		return rows
	}, [activeGrade, contactRows, sortDir, sortKey])

	return {
		visibleRows,
		tabs,
		activeGrade,
		setActiveGrade,
		sortKey,
		sortDir,
		setSort,
		studentSortOptions: STUDENT_SORT_OPTIONS,
		expandedIds,
		toggleExpand,
		visibleCount: visibleRows.length,
	}
}

export default useContacts
